import React, { useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/auth'
import Layout from './components/Layout'

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'))
const LandingPage = lazy(() => import('./pages/LandingPage'))
const EarlySignupPage = lazy(() => import('./pages/BetaSignupPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const DashboardV2Page = lazy(() => import('./pages/DashboardV2Page'))
const TablesV2Page = lazy(() => import('./pages/TablesV2Page'))
const CanvasV2Page = lazy(() => import('./pages/CanvasV2Page'))
const CanvasV2bPage = lazy(() => import('./pages/CanvasV2bPage'))
const ScreenerPage = lazy(() => import('./pages/ScreenerPage'))
const ScreenerV2Page = lazy(() => import('./pages/ScreenerV2Page'))
const StockDetailPage = lazy(() => import('./pages/StockDetailPage'))
const StockDetailV2Page = lazy(() => import('./pages/StockDetailV2Page'))
const WatchListPage = lazy(() => import('./pages/WatchListPage'))
const WatchListsV2Page = lazy(() => import('./pages/WatchListsV2Page'))
const AlertsV2Page = lazy(() => import('./pages/AlertsV2Page'))
const AgentsV2Page = lazy(() => import('./pages/AgentsV2Page'))
const AccountV2Page = lazy(() => import('./pages/AccountV2Page'))
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'))
const PortfolioV2Page = lazy(() => import('./pages/PortfolioV2Page'))
const TablesPage = lazy(() => import('./pages/TablesPage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'))
const UserManagementV2Page = lazy(() => import('./pages/UserManagementV2Page'))
const InfluencerManagementPage = lazy(() => import('./pages/InfluencerManagementPage'))
const InfluencerManagementV2Page = lazy(() => import('./pages/InfluencerManagementV2Page'))
const NewsPage = lazy(() => import('./pages/NewsPage'))
const StockPicksPage = lazy(() => import('./pages/StockPicksPage').then(module => ({ default: module.StockPicksPage })))
const InfluencersPage = lazy(() => import('./pages/InfluencersPage'))
const YouTubeSourcePage = lazy(() => import('./pages/YouTubeSourcePage'))

// Loading fallback component
const PageLoader = () => (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="spinner spinner-lg text-primary-500"></div>
    </div>
)

function App() {
    const { token, initialized, initializeAuth } = useAuthStore()

    useEffect(() => {
        console.log('[App] Mounting, calling initializeAuth')
        initializeAuth()
    }, [])

    useEffect(() => {
        console.log('[App] Token state changed:', !!token, 'initialized:', initialized)
    }, [token, initialized])

    // Don't render anything until auth is initialized
    if (!initialized) {
        console.log('[App] Still initializing, rendering loading state')
        return (
            <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
                <div className="spinner spinner-lg text-primary-500"></div>
            </div>
        )
    }

    return (
        <BrowserRouter>
            <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
                <Route path="/early-signup" element={<Suspense fallback={<PageLoader />}><EarlySignupPage /></Suspense>} />
                {!token && (
                    <Route path="/" element={<Suspense fallback={<PageLoader />}><LandingPage /></Suspense>} />
                )}

                {/* Protected Routes */}
                {token ? (
                    <>
                        {/* Dashboard v2 uses its own full Aero layout, not the shared Layout */}
                        <Route
                            path="/v2/dashboard"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <DashboardV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/tables"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <TablesV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/tables/:id"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <TablesV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/canvas"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <CanvasV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/canvas2"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <CanvasV2bPage />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/screener"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <ScreenerV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/watch-lists"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <WatchListsV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/watch-lists/:id"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <WatchListsV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/alerts"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <AlertsV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/agents"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <AgentsV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/account"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <AccountV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/user-management"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <UserManagementV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/influencer-management"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <InfluencerManagementV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/portfolios"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <PortfolioV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/portfolios/:portfolioId"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <PortfolioV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/stocks/:ticker"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <StockDetailV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/youtube/:source_id"
                            element={
                                <Suspense fallback={<PageLoader />}>
                                    <YouTubeSourcePage />
                                </Suspense>
                            }
                        />

                        {/* All other app pages use the existing Layout shell */}
                        <Route element={<Layout />}>
                            <Route
                                path="/"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <DashboardPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <DashboardPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/screener"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <ScreenerPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/stocks/:ticker"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <StockDetailPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/watch-lists"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <WatchListPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/watch-lists/:id"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <WatchListPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/portfolios"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <PortfolioPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/portfolios/:portfolioId"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <PortfolioPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/tables"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <TablesPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/tables/:id"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <TablesPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/news"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <NewsPage />
                                    </Suspense>
                                }
                            />
                            <Route path="/stock-picks" element={<Navigate to="/influencers" replace />} />
                            <Route
                                path="/influencers"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <InfluencersPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/influencers/:tab"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <InfluencersPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/user-management"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <UserManagementPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/influencer-management"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <InfluencerManagementPage />
                                    </Suspense>
                                }
                            />
                            <Route
                                path="/account"
                                element={
                                    <Suspense fallback={<PageLoader />}>
                                        <AccountPage />
                                    </Suspense>
                                }
                            />
                        </Route>
                    </>
                ) : (
                    <Route path="*" element={<Navigate to="/" replace />} />
                )}
            </Routes>
        </BrowserRouter>
    )
}

export default App
